<template>
  <ModalLayout
    :title="isRename ? 'Rename Container Type' : 'Add Container Type'"
  >
    <div
      class="relative rounded-t-[10px] bg-white px-[24px] pt-[41px] sm:px-[40px]"
    >
      <button
        type="button"
        aria-label="Close"
        class="absolute right-[24px] top-[36px] flex size-[32px] cursor-pointer items-center justify-center rounded-full border border-gpx_gray-800 sm:right-[40px]"
        @click.prevent="emit('close')"
      >
        <XMarkIcon
          class="heroicon-stroke-w-1.4 size-[20px] text-gpx_gray-200"
        />
      </button>

      <div class="border-b border-b-gpx_gray-800 pb-[18px]">
        <div class="mb-[2px] text-gpx_sm font-medium text-gpx_gray-200">
          Container Types
        </div>
        <div class="text-gpx_2xl font-semibold text-gpx_black-500">
          {{ isRename ? 'Rename this type' : 'Add a type' }}
        </div>
      </div>

      <form class="mt-[35px]" @submit.prevent="handleSave">
        <UIInput v-model="name" label="Name" placeholder="e.g. Steel rack" />
        <p class="mt-[4px] text-gpx_xs text-gpx_gray-100">
          {{
            isRename
              ? 'Every tracker carrying this type follows the new name, and so does its history.'
              : 'Trackers are assigned a type from their own page.'
          }}
        </p>
      </form>
    </div>

    <div class="rounded-b-[10px] bg-white px-[24px] py-[32px] sm:px-[40px]">
      <Button
        size="lg"
        loadingText="Saving"
        :loading="isSaving"
        :disabled="isSaving || !name.trim()"
        @click="handleSave"
      >
        {{ isRename ? 'Save name' : 'Add container type' }}
      </Button>
    </div>
  </ModalLayout>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { Button } from '@gpx-spa/ui';
import {
  createAccountContainerType,
  updateAccountContainerType,
} from '@gpx-spa/queries';
import ModalLayout from '@/layouts/ModalLayout.vue';
import UIInput from '@/components/ui/UIInput.vue';

const props = defineProps({
  params: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const toast = useToast();
const createMutation = createAccountContainerType();
const renameMutation = updateAccountContainerType();

const accountId = computed(() => store.getters['auth/accountId']);
const isRename = computed(() => !!props.params?.id);

const name = ref(props.params?.name ?? '');
const isSaving = ref(false);

const handleSave = async () => {
  const trimmed = name.value.trim();
  if (isSaving.value || !trimmed) return;

  isSaving.value = true;
  try {
    if (isRename.value) {
      await renameMutation.mutateAsync({
        accountId: accountId.value,
        id: props.params.id,
        name: trimmed,
      });
      toast.success('Container type renamed');
    } else {
      await createMutation.mutateAsync({
        accountId: accountId.value,
        name: trimmed,
      });
      toast.success('Container type added');
    }
    emit('close');
  } catch (e) {
    toast.error(
      e.response?.data?.message ||
        'Could not save the container type. Please try again later.',
    );
  } finally {
    isSaving.value = false;
  }
};
</script>
